import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Plus, Trophy, Video } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import type { Climb } from '../../types/climb';
import type { ClimbLogActionKind } from '../climbs/ClimbLogActions';

/** Reporting is not a log entry, so it rides beside the log actions. */
export type CragFabAction = ClimbLogActionKind | 'report';

interface CragTopoFabProps {
  /**
   * The route the button acts on — the one lit on the topo, or null when the
   * reader is on the info tab or has not picked a line yet. With no route the
   * button keeps only the actions that make sense for the crag itself.
   */
  climb: Climb | null;
  onAction: (kind: CragFabAction) => void;
}

interface FabAction {
  kind: CragFabAction;
  label: string;
  icon: typeof Plus;
  className: string;
}

/**
 * The one button that logs a go, floating over the topo and pointing at
 * whichever route is open.
 *
 * One button rather than one per row: on a cliff a climber is on a single line
 * at a time, and a column of twenty identical buttons says nothing about which
 * one they are on. It carries the route's name for exactly that reason — a
 * floating button that acts on a selection has to say what the selection is.
 *
 * Pressing it fans its actions out above itself, the same way the climb
 * profile's FAB does, rather than throwing a sheet over the topo: the picture
 * and the lit line are what the choice is being made about, so they stay in
 * sight while it is made.
 *
 * It turns green once the route is in your book, so the topo answers "have I
 * done this one" without opening anything.
 *
 * Reporting is the one action that survives having no route selected: the
 * parking, the path and the access notice are not anybody's line, and a
 * climber who wants to flag one of those should not have to light a random
 * route first.
 */
export function CragTopoFab({ climb, onAction }: CragTopoFabProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const logActions: FabAction[] = climb
    ? [
        {
          kind: 'send',
          label: t('cragProfile.logSend'),
          icon: Trophy,
          className: 'bg-green-600 text-white hover:bg-green-700',
        },
        {
          kind: 'try',
          label: t('cragProfile.logAttempt'),
          icon: Plus,
          className: 'bg-primary text-primary-foreground hover:bg-primary/90',
        },
        {
          kind: 'video',
          label: t('cragProfile.uploadVideo'),
          icon: Video,
          className: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        },
      ]
    : [];

  const actions: FabAction[] = [
    ...logActions,
    {
      kind: 'report',
      // Names the route when there is one, so the reporter can see what the
      // form is about to be prefilled with before they open it.
      label: climb ? t('cragReports.reportClimb', { name: climb.name }) : t('cragReports.reportCrag'),
      icon: AlertTriangle,
      className: 'bg-amber-500 text-white hover:bg-amber-600',
    },
  ];

  return (
    <div
      className={cn(
        'fixed z-50 flex items-center gap-2',
        'right-4 md:right-6',
        'bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6'
      )}
    >
      {/* Which route the actions will land on. Hidden while they are showing —
          each one names itself, and the two rows of labels would collide. */}
      {!open && climb && (
        <span className="max-w-[45vw] truncate rounded-md border bg-background/95 px-3 py-1.5 text-sm font-medium shadow">
          {climb.name}
        </span>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-label={climb ? t('cragProfile.logOnClimb', { name: climb.name }) : t('cragProfile.cragActions')}
            className={cn(
              'h-14 w-14 rounded-full p-0 shadow-lg',
              'data-[state=open]:rotate-45 transition-transform duration-200',
              climb?.user_topped && 'bg-green-600 text-white hover:bg-green-700'
            )}
          >
            {climb?.user_topped && !open ? <Check className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          sideOffset={12}
          className="w-auto border-none bg-transparent p-0 shadow-none"
        >
          <div className="flex flex-col items-end gap-3">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.kind}
                  type="button"
                  onClick={() => { setOpen(false); onAction(action.kind); }}
                  className="group flex items-center gap-2"
                >
                  <span className="max-w-[60vw] truncate rounded-md border bg-background/95 px-3 py-1.5 text-sm font-medium text-foreground shadow">
                    {action.label}
                  </span>
                  <span
                    className={cn(
                      'inline-flex h-12 w-12 items-center justify-center rounded-full shadow-md transition-transform group-hover:scale-105',
                      action.className
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
